import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ImpresoraApiService } from '@core/services/api/impresora-api.service';
import { AppService } from '@shared/services/app.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { IOptionsSelect } from '@core/interfaces/options.select.interface';
import { UiSelect } from '@shared/components/ui';
import { FormsModule } from '@angular/forms';
import { ImpresoraService } from '@shared/services/impresora.service';
import { logger } from '@shared/utils/functions.utils';
import { IConfiguracionImpresora } from '@core/interfaces/entidades/configuracion-impresora.entidad';
import { SucursalListenerService } from '@shared/services/websocket/sucursal-listener.service';
import { EVENT_GATEWAY } from '@core/enums/eventWebSocket.enum';
import { ID_DESCARGA_PDF } from '@shared/constants/impresora.const';

@Component({
  selector: 'app-selector-impresora',
  imports: [UiSelect, FormsModule, CommonModule],
  templateUrl: './selector-impresora.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './selector-impresora.css',
})
export class SelectorImpresora implements OnInit {
  constructor() {
    effect(() => {
      if (this.appService.authReady() && this.sucursalService.sucursalReady()) {
        this.cargarImpresoras();
      }
    });
  }
  // servicios
  private readonly impresoraApiService = inject(ImpresoraApiService);
  private readonly appService = inject(AppService);
  private readonly sucursalService = inject(SucursalService);
  private readonly impresoraService = inject(ImpresoraService);
  private readonly sucursalListenerService = inject(SucursalListenerService);

  // datos
  readonly ID_DESCARGA_PDF = ID_DESCARGA_PDF;
  impresora = signal<number | null>(null);
  opcionesImpresora = signal<IOptionsSelect<number | null>[]>([]);

  opcionesImpresoraComputed = computed(() =>
    this.opcionesImpresora().map((opt) => {
      let labelExtra = '';
      if (opt.value === null) {
        labelExtra = 'Sin impresora';
      } else if (opt.value === ID_DESCARGA_PDF) {
        labelExtra = `Descargar PDF (${opt.value})`;
      } else {
        labelExtra = `${opt.label}`;
      }
      return { label: labelExtra, value: opt.value };
    }),
  );

  loading = signal(true);
  conError = signal(false);

  private cargarImpresoras() {
    this.loading.set(true);
    this.conError.set(false);
    this.impresoraApiService.cargarImpresoras().subscribe({
      next: (res) => {
        this.actualizarOpcionesImpresora(res);
      },
      error: (err) => {
        console.error('Error cargando impresoras sdsds:', err);
        this.opcionesImpresora.set([{ label: 'Sin impresora', value: null }]);
        this.conError.set(true);
      },
      complete: () => {
        this.loading.set(false);
      },
    });
  }

  private seleccionarUltimaImpresoraSeleccionada() {
    const ultimaImpresoraId = this.sucursalService.getUltimaImpresoraId();
    logger('Última impresora seleccionada:', ultimaImpresoraId);
    if (ultimaImpresoraId != null) {
      const existe = this.opcionesImpresora().some((opt) => opt.value == ultimaImpresoraId);
      if (existe) {
        logger('Restaurando última impresora seleccionada:', ultimaImpresoraId);
        this.seleccionarImpresora(ultimaImpresoraId, false);
      }
    }
  }

  ngOnInit(): void {
    this.sucursalListenerService
      .onEvent<IConfiguracionImpresora[]>(EVENT_GATEWAY.SUCURSAL_PRINT_UPDATE)
      .subscribe((object) => {
        this.actualizarOpcionesImpresora(object.data);
      });

    this.impresoraService.impresoraSeleccionada$.subscribe((id) => {
      this.impresora.set(id);
    });
  }
  onChange(value: number | null) {
    logger('Impresora seleccionada:', value);
    const idImpresoraSeleccionada = value;
    this.seleccionarImpresora(idImpresoraSeleccionada);
  }

  private actualizarOpcionesImpresora(impresoras: IConfiguracionImpresora[]) {
    logger('Actualizando opciones de impresora:', impresoras);
    const opciones = impresoras.map((imp) => ({
      label: imp.nombre,
      value: imp.id,
    }));
    this.opcionesImpresora.set([
      { label: 'Sin impresora', value: null },
      { label: 'Descargar PDF', value: ID_DESCARGA_PDF },
      ...opciones,
    ]);

    const seleccionActual = this.impresora();
    const seleccionSigueDisponible = this.opcionesImpresora().some(
      (opt) => opt.value === seleccionActual,
    );

    if (seleccionActual != null && !seleccionSigueDisponible) {
      this.seleccionarImpresora(null, false);
    }

    this.seleccionarUltimaImpresoraSeleccionada();
  }

  private seleccionarImpresora(idImpresora: number | null, guardarEnStorage = true) {
    this.impresora.set(idImpresora);
    this.impresoraService.setImpresoraSeleccionada(idImpresora);
    if (guardarEnStorage) {
      this.sucursalService.setUltimaImpresoraId(idImpresora);
    }
  }
}
